import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  useCreateTravelEsimTariff,
  useUpdateTravelEsimTariff,
} from '@/hooks/useTravelEsimTariffActions';
import { zodResolver } from '@hookform/resolvers/zod';
import type { TravelEsimTariff } from '@octave/contracts';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const TariffFormSchema = z
  .object({
    country: z.string().trim().min(1, 'Indiquez la destination'),
    unlimited: z.boolean(),
    dataLimitGb: z.coerce.number().int().positive('Indiquez la data en Go').optional(),
    durationDays: z.coerce.number().int().positive('Indiquez la durée en jours'),
    priceEuros: z.coerce.number().nonnegative('Indiquez le prix HT'),
  })
  .superRefine((form, ctx) => {
    if (!form.unlimited && !form.dataLimitGb) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dataLimitGb'],
        message: 'Indiquez la data en Go, ou cochez Internet illimité',
      });
    }
  });

type TariffForm = z.infer<typeof TariffFormSchema>;

interface TravelEsimTariffDialogProps {
  /** Existing row to change; none to add one. */
  tariff?: TravelEsimTariff;
  onClose: () => void;
}

/** Add or change one offer of the international eSIM grid (Octave admin). */
export function TravelEsimTariffDialog({ tariff, onClose }: TravelEsimTariffDialogProps) {
  const create = useCreateTravelEsimTariff();
  const update = useUpdateTravelEsimTariff();
  const isPending = create.isPending || update.isPending;

  const form = useForm<TariffForm>({
    resolver: zodResolver(TariffFormSchema),
    defaultValues: {
      country: tariff?.country ?? '',
      unlimited: tariff ? tariff.dataLimitGb === null : false,
      dataLimitGb: tariff?.dataLimitGb ?? undefined,
      durationDays: tariff?.durationDays ?? 7,
      priceEuros: tariff ? tariff.priceCents / 100 : 0,
    },
  });
  const unlimited = form.watch('unlimited');

  const submit = form.handleSubmit(async (values) => {
    const body = {
      country: values.country,
      dataLimitGb: values.unlimited ? null : (values.dataLimitGb ?? null),
      durationDays: values.durationDays,
      priceCents: Math.round(values.priceEuros * 100),
    };
    if (tariff) {
      await update.mutateAsync({ id: tariff.id, ...body });
    } else {
      await create.mutateAsync(body);
    }
    onClose();
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">
            {tariff ? 'Modifier le forfait' : 'Ajouter un forfait'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Destination" error={form.formState.errors.country?.message}>
            <Input placeholder="Argentine" className="h-11 rounded-xl" {...form.register('country')} />
          </Field>

          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-slate-700">Internet illimité</p>
              <p className="text-xs text-slate-400">Sinon, indiquez la data en Go.</p>
            </div>
            <Switch
              checked={unlimited}
              onCheckedChange={(checked) => form.setValue('unlimited', checked, { shouldValidate: true })}
            />
          </div>
          {!unlimited && (
            <Field label="Data (Go)" error={form.formState.errors.dataLimitGb?.message}>
              <Input type="number" min={1} step={1} className="h-11 rounded-xl" {...form.register('dataLimitGb')} />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Durée (jours)" error={form.formState.errors.durationDays?.message}>
              <Input type="number" min={1} step={1} className="h-11 rounded-xl" {...form.register('durationDays')} />
            </Field>
            <Field label="Prix HT (€)" error={form.formState.errors.priceEuros?.message}>
              <Input type="number" min={0} step={0.01} className="h-11 rounded-xl" {...form.register('priceEuros')} />
            </Field>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5 gap-2"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {tariff ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
